import { FormCheckbox, FormHelperText, FormLocationSelector, FormSelect, FormTextInput } from "components/form-fields";
import { ShowHide } from "components/layout";
import { ListingTable } from "components/listing";
import { useValidation } from "hooks/useValidation";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { UserService } from "services";
import { AlertConfirm } from "components/notifications";
import { useDebounce } from "hooks/useDebounce";
import { TagList } from "components/status";

export function ManualEntryTemplatePermissions({ template, onValidChange, options, loading, saving, onTemplateUpdate, readOnly = false }) {

    const [permissionTypes, setPermissionTypes] = useState([]);

    const [showLocationsAsHierarchy, setShowLocationsAsHierarchy] = useState(true);
    const [locations, setLocations] = useState({ location: [{ items: [] }] });
    const [loadingLocations, setLoadingLocations] = useState(true);
    const [locationTableRows, setLocationTableRows] = useState([]);
    const [locationRowsCount, setLocationRowsCount] = useState(0);
    const readOnlyLocationTableHeaders = ["Name"];

    const [selectedEmails, setSelectedEmails] = useState([]);
    const [users, setUsers] = useState([]);
    const [userTableRows, setUserTableRows] = useState([]);
    const [totalUserRecords, setTotalUserRecords] = useState(1);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [pageNumber, setPageNumber] = useState(1);
    const pageSize = 25;
    const readOnlyUsersTableHeaders = ["Name", "Email"];

    const debounceTimeout = 750;
    const inputSearchRef = useRef();
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);
    const selectUsersTableHeaders = ["Email", "Name", "Select"];

    const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

    const [isValid, errors, validate] = useValidation(false);

    useEffect(() => {

        setPermissionTypes(options?.accessTypes?.map(type => {
            return { id: type.key, name: type.name, };
        }) ?? []);

    }, [options]);

    useEffect(() => {
        setSearchTerm("");
        if (template?.accessType == null) {
            return;
        }

        if (template.accessType === "USER") {
            fetchUserData();
        }

        if (template.accessType === "LOCATION") {
            setLoadingLocations(true);

            UserService.getLoggedInUserLocations(1, false, true).then((response) => {
                setLocations(response);
                setLoadingLocations(false);
            });
        }

    }, [template?.accessType]);

    useEffect(() => {
        const rules = [
            { fieldName: "accessType", required: true }
        ];
        const externalRules = [
            ...(template?.accessType === "USER" ? [{ fieldName: "access", valid: template?.access?.length > 0, errorMessage: "Add at least one user." }] : []),
            ...(template?.accessType === "LOCATION" ? [{ fieldName: "access", valid: template?.access?.length > 0, errorMessage: "Add at least one location." }] : [])
        ];
        validate(rules, template, externalRules);
    }, [template?.accessType, template?.access]);

    useEffect(function () {
        onValidChange(isValid);
    }, [isValid]);

    useEffect(mapUsersTableData, [template?.access, template?.users, template?.accessType, users, readOnly, saving]);
    useEffect(mapLocationsTableData, [template?.access, template?.accessType, locations, readOnly, saving]);

    useEffect(() => {
        fetchUserData();
    }, [pageSize, pageNumber, debouncedSearchTerm]);

    useEffect(() => {
        setPageNumber(1);
    }, [debouncedSearchTerm]);

    function onPermissionsSelect(item) {
        onTemplateUpdate({ accessType: item.id, access: [], users: [] })
    }

    function onOneAccessIdSelect(id) {
        let newAccessList = [...(template?.access ?? [])];
        let templateUsers = [...(template?.users ?? [])];

        if (newAccessList.includes(id)) {
            newAccessList = newAccessList.filter(x => x !== id);
            templateUsers = templateUsers.filter(u => u.id !== id);
        } else {
            newAccessList.push(id);
            const user = users?.find(u => u.userId === id);
            templateUsers.push({ id: user.userId, name: `${user.firstName} ${user.lastName}`, email: user.emailAddress });
        }

        onTemplateUpdate({ access: newAccessList, users: templateUsers });
    }

    function onUserSearchChange(item) {
        setSearchTerm(item.target.value);
    }

    function onPagingChange(newPageNumber) {
        setPageNumber(newPageNumber);
    }

    function fetchUserData() {
        setLoadingUsers(true);

        UserService.getUserListWebsite(pageSize, pageNumber, debouncedSearchTerm, "Email", "A", []).then((response) => {
            setUsers(response.data);
            setTotalUserRecords(response.totalCount);
            setLoadingUsers(false);
            setTimeout(function () {
                if (inputSearchRef.current != null) {
                    inputSearchRef.current.focus();
                }
            });
        });
    }

    function onLocationSelect(allSelectedlocations) {
        onTemplateUpdate({ access: [...allSelectedlocations] });
    }

    function setUseHierarchy() {
        setShowLocationsAsHierarchy(previous => !previous);
    }

    function onUserDeleteClicked(_, id) {
        setUserToDelete(template?.users?.find(user => user.id === id));
        setShowDeleteUserModal(true);
    }

    function onDeleteUserModalConfirm() {
        onOneAccessIdSelect(userToDelete.id);
        setShowDeleteUserModal(false);
        setUserToDelete(null);
    }

    function onDeleteUserModalCancel() {
        setShowDeleteUserModal(false);
        setUserToDelete(null);
    }

    function mapUsersTableData() {
        if (template?.accessType !== "USER") return;

        const selectedUsers = template?.users?.filter(user => template?.access?.includes(user.id)) ?? [];

        if (readOnly) {
            const newUserTableRows = selectedUsers.map(user => {
                return [
                    user.name,
                    user.email,
                ]
            }) ?? [];
            setUserTableRows(newUserTableRows);
        } else {
            setUserTableRows(users.map(user => {
                return [
                    user.emailAddress,
                    `${user.firstName} ${user.lastName}`,
                    <FormCheckbox key={`select-user-${user.userId}`} disabled={saving} onChange={() => onOneAccessIdSelect(user.userId)} checked={template?.access?.includes(user.userId)} />
                ]
            }));
            setSelectedEmails(selectedUsers.map(u => ({ id: u.id, name: u.email })));
        }
    }

    function mapLocationsTableData() {
        if (template?.accessType !== "LOCATION") return;

        const filteredLocations = locations?.location[0]?.items?.filter(location => template?.access?.includes(location.id));
        const newLocationTableRows = filteredLocations.map(location => {
            return [location.name]
        }) ?? [];

        setLocationTableRows(newLocationTableRows);
        setLocationRowsCount(newLocationTableRows.length);
    }

    return (
        <>
            <FormSelect
                id="settings-permission-type"
                headingText={readOnly ? undefined : "Please select who will be able to access and use this manual entry template"}
                loading={loading}
                disabled={saving || readOnly}
                placeholder="Please select"
                items={permissionTypes}
                onItemSelect={onPermissionsSelect}
                selectedValue={template.accessType}
                dangerHelperText={errors["accessType"]}
            />

            {template.accessType === "LOCATION" && (
                <Fragment>
                    <h2>Locations</h2>
                    <ShowHide
                        evaluator={readOnly}
                        show={
                            <ListingTable
                                id="listing-table-locations"
                                headers={readOnlyLocationTableHeaders}
                                data={locationTableRows}
                                totalRecordCount={locationRowsCount}
                                loadingData={loadingLocations}
                                noDataMessage={"No locations added"}
                                sortable
                                sortableHeaders={[]}
                                sortedBy={"Name"}
                                sortedDir={"A"}
                                showPerPage={false}
                                shrinkLastColumn
                                pageable={false}
                                allowTableScrollVertical
                            />
                        }
                        hide={
                            <div className="spacer-bottom">
                                <FormLocationSelector
                                    headingText={``}
                                    businessArea={"Tacho"}
                                    loading={loading || loadingLocations}
                                    selectedLocations={template.access ?? []}
                                    setSelectedLocations={onLocationSelect}
                                    useHierarchy={showLocationsAsHierarchy}
                                    setUseHierarchy={setUseHierarchy}
                                    useLocationDefaults={false}
                                    useCustomLocations
                                    customLocations={locations}
                                    locationDefaultsToTrue
                                    disabled={saving || readOnly}
                                />
                            </div>
                        }
                    />
                </Fragment>
            )}

            {template.accessType === "USER" && (
                <Fragment>
                    <h2>Users</h2>
                    <ShowHide
                        evaluator={readOnly}
                        show={
                            <div className="spacer-bottom">
                                <ListingTable
                                    id="listing-table-user"
                                    headers={readOnlyUsersTableHeaders}
                                    data={userTableRows}
                                    totalRecordCount={totalUserRecords}
                                    loadingData={loadingUsers}
                                    noDataMessage={"No users added"}
                                    sortable
                                    sortableHeaders={[]}
                                    sortedBy={"Name"}
                                    sortedDir={"A"}
                                    showPerPage={false}
                                    shrinkLastColumn
                                    pageable={false}
                                    allowTableScrollVertical
                                />
                            </div>
                        }
                        hide={
                            <div className="spacer-bottom">
                                <TagList items={selectedEmails} disabled={saving || loadingUsers} onRemoveTag={onUserDeleteClicked} />
                                <h2>Select Users</h2>
                                <div className="inline-items spacer-bottom-small">
                                    <div className="inline-item fill">
                                        <div className="form-field-full">
                                            <FormTextInput inputRef={inputSearchRef} placeholder="Search Users" onChange={onUserSearchChange} value={searchTerm} large disabled={loadingUsers || saving} icon="search" id="user-search-access" />
                                        </div>
                                    </div>
                                </div>

                                <ListingTable
                                    id="listing-table-user"
                                    headers={selectUsersTableHeaders}
                                    data={userTableRows}
                                    totalRecordCount={totalUserRecords}
                                    onPagingChange={onPagingChange}
                                    loadingData={loadingUsers}
                                    noDataMessage={"No users found"}
                                    sortable
                                    sortableHeaders={[]}
                                    sortedBy={"Name"}
                                    sortedDir={"A"}
                                    showPerPage={false}
                                    shrinkLastColumn={true}
                                />
                            </div>
                        }
                    />

                    <AlertConfirm
                        isOpen={showDeleteUserModal}
                        title='Delete User'
                        confirmButtonIntent='primary'
                        onCancel={onDeleteUserModalCancel}
                        onConfirm={onDeleteUserModalConfirm}
                    >
                        <p>{`Are you sure you want to delete `}<b>{userToDelete?.name}</b>{` from this Manual Entry Template?`}</p>
                    </AlertConfirm>
                </Fragment>
            )}

            {errors["access"] && (
                <div className="form-row spacer-bottom-small">
                    <FormHelperText loading={loading} danger={true}>{errors["access"]}</FormHelperText>
                </div>
            )}

        </>
    );
}